import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { Booking } from '../booking';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import Swal from 'sweetalert2';
import ViewFinalBookingModal from './ViewFinalBookingModal';

interface FinalApprovedBookingsModalProps {
  show: boolean;
  onHide: () => void;
  finalApprovedDetails: Booking[];
  finalApprovedModalError: string | null;
  statusColors: { [key: string]: string };
  handleFinalApprove: (bookingId: number, driverId: string | null, vehicleId: string | null, comment: string, status: string) => Promise<boolean>;
}

interface Driver {
  empNumber: string;
  name: string;
}

interface Vehicle {
  numberPlate: string;
  model: string;
}

interface RawDriverData {
  empNumber: string | number;
  name: string;
  email?: string;
  phone?: string;
}

interface RawVehicleData {
  vehicleId?: string | number;
  numberPlate?: string | number;
  vehicleModel?: string;
  model?: string;
}

const FinalApprovedBookingsModal: React.FC<FinalApprovedBookingsModalProps> = ({
  show,
  onHide,
  finalApprovedDetails,
  finalApprovedModalError,
  statusColors,
  handleFinalApprove,
}) => {
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [formData, setFormData] = useState<{ [key: number]: { driverId: string; vehicleId: string; comment: string } }>({});
  const [isLoadingDrivers, setIsLoadingDrivers] = useState<boolean>(false);
  const [isLoadingVehicles, setIsLoadingVehicles] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showViewModal, setShowViewModal] = useState<boolean>(false);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);

  useEffect(() => {
  }, [show, finalApprovedDetails, drivers, vehicles]);

  const isBookingToday = (departureTime: number | null | undefined): boolean => {
    if (!departureTime) return true;
    const bookingDate = new Date(departureTime);
    const today = new Date();
    return (
      bookingDate.getDate() === today.getDate() &&
      bookingDate.getMonth() === today.getMonth() &&
      bookingDate.getFullYear() === today.getFullYear()
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!show) return;

      setIsLoadingDrivers(true);
      setIsLoadingVehicles(true);
      setError(null);

      try {
        let allDrivers: Driver[] = [];
        for (const booking of finalApprovedDetails) {
          const departureTime = booking.departureTime ? Number(booking.departureTime) : undefined;
          const arrivalTime = booking.arrivalTime ? Number(booking.arrivalTime) : undefined;

          // Convert times to millisecond timestamps as strings, or undefined if invalid
          const departureTimeStr = departureTime && !isNaN(departureTime) ? departureTime.toString() : undefined;
          const arrivalTimeStr = arrivalTime && !isNaN(arrivalTime) ? arrivalTime.toString() : undefined;

          if (!departureTimeStr) {
            setError((prev) => prev || `Departure time is required for booking ${booking.id}.`);
            Swal.fire({
              icon: 'warning',
              title: 'Missing Departure Time',
              text: `Departure time is required for booking ${booking.id}.`,
            });
            continue;
          }

          try {
            const driverResponse = await apiDefinitions.getAvailableDrivers(departureTimeStr, arrivalTimeStr);

            if (driverResponse.data.status !== 'S1000') { // Updated to check for 'S1000'
              setError((prev) => prev || driverResponse.data.statusDescription || `Error fetching drivers for booking ${booking.id}.`);
              Swal.fire({
                icon: 'error',
                title: 'API Error',
                text: driverResponse.data.statusDescription || `Failed to fetch drivers for booking ${booking.id}.`,
              });
              continue;
            }

            const driverData: RawDriverData[] = driverResponse.data?.data?.drivers || [];
            if (!Array.isArray(driverData)) {
              setError((prev) => prev || `Invalid driver data for booking ${booking.id}.`);
              Swal.fire({
                icon: 'warning',
                title: 'Invalid Driver Data',
                text: `Driver data for booking ${booking.id} is not in the expected format.`,
              });
              continue;
            }
            if (driverData.length === 0) {
              setError((prev) => prev || `No drivers available for booking ${booking.id}.`);
              Swal.fire({
                icon: 'warning',
                title: 'No Drivers Found',
                text: `No drivers are available for booking ${booking.id}.`,
              });
            } else {
              const mappedDrivers = driverData
                .map((d: RawDriverData) => ({
                  empNumber: d.empNumber.toString(),
                  name: d.name || 'Unknown',
                }))
                .filter((d: Driver) => d.empNumber && d.empNumber !== '');
              allDrivers = [...allDrivers, ...mappedDrivers];
            }
          } catch (err: any) {
            const errorMsg = err.response?.data?.statusDescription || err.response?.data?.msg || 'Failed to fetch drivers.';
            setError((prev) => prev || errorMsg);
            Swal.fire({
              icon: 'error',
              title: 'Fetch Error',
              text: errorMsg,
            });
          }
        }

        const uniqueDrivers = Array.from(
          new Map(allDrivers.map((driver) => [driver.empNumber, driver])).values()
        );
        setDrivers(uniqueDrivers);
        if (uniqueDrivers.length === 0) {
          
        }
      } catch (err: any) {
        const errorMsg = err.response?.data?.statusDescription || 'Failed to fetch drivers.';
        setError(errorMsg);
        Swal.fire({
          icon: 'error',
          title: 'Fetch Error',
          text: errorMsg,
        });
      } finally {
        setIsLoadingDrivers(false);
      }

      try {
  let allVehicles: Vehicle[] = [];

  for (const booking of finalApprovedDetails) {
    const departureTime = booking.departureTime ? Number(booking.departureTime) : undefined;
    const arrivalTime = booking.arrivalTime ? Number(booking.arrivalTime) : undefined;
    const totalNoOfPassengers = booking.noOfPassengers || 1;

    if (!departureTime || !arrivalTime) {
      continue;
    }

    try {
      const vehicleResponse = await apiDefinitions.getAvailableVehicles(
        departureTime,
        arrivalTime,
        totalNoOfPassengers
      );

      const vehicleData: RawVehicleData[] = vehicleResponse.data?.data?.vehicles || [];

      if (!Array.isArray(vehicleData) || vehicleData.length === 0) {
        Swal.fire({
          icon: 'warning',
          title: 'No Vehicles',
          text: `No vehicles available for booking ${booking.id}.`,
        });
        continue;
      }

      const mapped = vehicleData
        .map((v: RawVehicleData) => ({
          numberPlate: (v.numberPlate || '').toString(),
          model: v.vehicleModel || v.model || 'N/A',
        }))
        .filter((v) => v.numberPlate);

      allVehicles = [...allVehicles, ...mapped];
    } catch (err: any) {
      const errorMsg = err.message || `Failed to fetch vehicles for booking ${booking.id}.`;
      Swal.fire({
        icon: 'error',
        title: 'Vehicle Fetch Error',
        text: errorMsg,
      });
    }
  }

  const uniqueVehicles = Array.from(new Map(allVehicles.map((v) => [v.numberPlate, v])).values());
  setVehicles(uniqueVehicles);

  if (uniqueVehicles.length === 0) {

  }
} catch (err: any) {
  const errorMsg = err.message || 'Failed to fetch vehicles.';
  setError(errorMsg);
  Swal.fire({
    icon: 'error',
    title: 'Vehicle Fetch Error',
    text: errorMsg,
  });
}
 finally {
        setIsLoadingVehicles(false);
      }
    };

    fetchData();
  }, [show, finalApprovedDetails]);

  useEffect(() => {
    const initialFormData = finalApprovedDetails.reduce(
      (acc, booking) => {
        acc[booking.id] = {
          driverId: booking.driverId || '',
          vehicleId: booking.vehicleId || '',
          comment: booking.finalComment || '',
        };
        return acc;
      },
      {} as { [key: number]: { driverId: string; vehicleId: string; comment: string } }
    );
    setFormData(initialFormData);
  }, [finalApprovedDetails]);

  const handleInputChange = (bookingId: number, field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [bookingId]: {
        ...prev[bookingId],
        [field]: value,
      },
    }));
  };

  const handleViewClick = (booking: Booking) => {
    setSelectedBooking(booking);
    setShowViewModal(true);
  };

  const handleApproveClick = async (bookingId: number) => {
    if (!finalApprovedDetails.some((b) => b.id === bookingId)) {
      setError(`Booking ${bookingId} not found in final approved details.`);
      Swal.fire({
        icon: 'error',
        title: 'Booking Error',
        text: `Booking ${bookingId} not found in final approved details.`,
      });
      return;
    }

    const { driverId, vehicleId, comment } = formData[bookingId] || {};
    if (!driverId || !vehicleId) {
      setError('Please select both driver and vehicle.');
      Swal.fire({
        icon: 'error',
        title: 'Missing Selection',
        text: 'Please select both a driver and a vehicle before approving.',
      });
      return;
    }

    setIsLoadingDrivers(true);
    setError(null);
    try {
      const success = await handleFinalApprove(bookingId, driverId, vehicleId, comment || 'Approved by manager', 'FINAL_APPROVED');
      if (success) {
        Swal.fire({
          icon: 'success',
          title: 'Booking Approved',
          text: `Booking ${bookingId} successfully approved.`,
        });
        setFormData((prev) => ({
          ...prev,
          [bookingId]: { driverId: '', vehicleId: '', comment: '' },
        }));
      } else {
        setError('Failed to approve booking.');
        Swal.fire({
          icon: 'error',
          title: 'Approval Failed',
          text: 'Failed to approve booking.',
        });
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.status || err.response?.data?.msg || 'Failed to approve booking.';
      setError(errorMsg);
      Swal.fire({
        icon: 'error',
        title: 'Approval Error',
        text: errorMsg,
      });
    } finally {
      setIsLoadingDrivers(false);
    }
  };

  const handleRejectClick = async (bookingId: number) => {
    if (!finalApprovedDetails.some((b) => b.id === bookingId)) {
      setError(`Booking ${bookingId} not found in final approved details.`);
      Swal.fire({
        icon: 'error',
        title: 'Booking Error',
        text: `Booking ${bookingId} not found in final approved details.`,
      });
      return;
    }

    const { comment } = formData[bookingId] || {};
    if (!comment.trim()) {
      setError('Please provide a reason for rejection.');
      Swal.fire({
        icon: 'error',
        title: 'Missing Comment',
        text: 'Please provide a reason for rejecting the booking.',
      });
      return;
    }

    setIsLoadingDrivers(true);
    setError(null);
    try {
      const success = await handleFinalApprove(
        bookingId,
        null,
        null,
        comment.trim() || 'Rejected by service manager',
        'FINAL_REJECTED'
      );
      if (success) {
        Swal.fire({
          icon: 'success',
          title: 'Booking Rejected',
          text: `Booking ${bookingId} successfully rejected.`,
        });
        setFormData((prev) => ({
          ...prev,
          [bookingId]: { driverId: '', vehicleId: '', comment: '' },
        }));
      } else {
        setError('Failed to reject booking.');
        Swal.fire({
          icon: 'error',
          title: 'Rejection Failed',
          text: 'Failed to reject booking. Please try again.',
        });
      }
    } catch (err: any) {
      const errorMsg =
        err.response?.data?.status || err.response?.data?.msg || 'Failed to reject booking. Please try again.';
      setError(errorMsg);
      Swal.fire({
        icon: 'error',
        title: 'Rejection Error',
        text: errorMsg,
      });

    } finally {
      setIsLoadingDrivers(false);
    }
  };

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        size="xl"
        autoFocus={false}
        enforceFocus={false}
        aria-labelledby="final-approved"
      >
        <Modal.Header closeButton>
          <Modal.Title id="final-approved">Final Booking Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(isLoadingDrivers || isLoadingVehicles) && <div>Loading...</div>}
          {error && <Alert variant="danger">{error}</Alert>}
          {finalApprovedModalError && <Alert variant="danger">{finalApprovedModalError}</Alert>}
          {!isLoadingDrivers && !isLoadingVehicles && !finalApprovedModalError && finalApprovedDetails.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Emp</th>
                    <th>Name</th>
                    <th>Priority</th>
                    <th>Created</th>
                    <th>Driver</th>
                    <th>Vehicle</th>
                    <th>Comment</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {finalApprovedDetails.map((booking) => (
                    <tr key={booking.id}>
                      <td>{booking.id}</td>
                      <td>{booking.user?.empNumber || 'N/A'}</td>
                      <td>
                      {booking.user?.name.split(' ').slice(-3).join(' ')}
                      </td>
                      <td>{booking.priority || 'N/A'}</td>
                      <td>{new Date(booking.createdAt).toLocaleDateString()}</td>
                      <td>
                        <Form.Select
                          value={formData[booking.id]?.driverId || ''}
                          onChange={(e) => handleInputChange(booking.id, 'driverId', e.target.value)}
                        >
                          <option value="">Select Driver</option>
                          {drivers.map((driver) => (
                            <option key={driver.empNumber} value={driver.empNumber}>
                              {driver.name} ({driver.empNumber})
                            </option>
                          ))}
                        </Form.Select>
                        {drivers.length === 0 && !isLoadingDrivers && (
                          <Form.Text className="text-danger">No drivers available.</Form.Text>
                        )}
                      </td>
                      <td>
                        <Form.Select
                          value={formData[booking.id]?.vehicleId || ''}
                          onChange={(e) => handleInputChange(booking.id, 'vehicleId', e.target.value)}
                        >
                          <option value="">Select Vehicle</option>
                          {vehicles.map((vehicle) => (
                            <option key={vehicle.numberPlate} value={vehicle.numberPlate}>
                             {vehicle.numberPlate}
                            </option>
                          ))}
                        </Form.Select>
                        {vehicles.length === 0 && !isLoadingVehicles && (
                          <Form.Text className="text-danger">No vehicles available.</Form.Text>
                        )}
                      </td>
                      <td>
                        <Form.Control
                          as="textarea"
                          rows={1}
                          value={formData[booking.id]?.comment || ''}
                          onChange={(e) => handleInputChange(booking.id, 'comment', e.target.value)}
                          placeholder="Reasons "
                        />
                      </td>
                      <td>
                        <div className="hstack gap-2">
                          <Button
                            variant="success"
                            size="sm"
                            onClick={() => handleApproveClick(booking.id)}
                            disabled={
                              isLoadingDrivers ||
                              isLoadingVehicles ||
                              !formData[booking.id]?.driverId ||
                              !formData[booking.id]?.vehicleId
                            }
                          >
                            Approve
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleRejectClick(booking.id)}
                            disabled={isLoadingDrivers || isLoadingVehicles || !formData[booking.id]?.comment?.trim()}
                          >
                            Reject
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            className="btn-icon rounded-circle"
                            onClick={() => handleViewClick(booking)}
                            title="View Booking"
                          >
                            <i className="feather-eye" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            !isLoadingDrivers &&
            !isLoadingVehicles && <Alert variant="info">No Pending Final Approved Bookings Available</Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={onHide} disabled={isLoadingDrivers || isLoadingVehicles}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      {selectedBooking && (
        <ViewFinalBookingModal
          show={showViewModal}
          onHide={() => {
            setShowViewModal(false);
            setSelectedBooking(null);
          }}
          booking={selectedBooking}
          statusColors={statusColors}
        />
      )}
    </>
  );
};

export default FinalApprovedBookingsModal;